import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from "rxjs";

// PrimeNG imports
import { ButtonModule } from "primeng/button";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { SelectModule } from "primeng/select";

// Models
import {
  IProductCounts,
  IProductFilterControls,
  IProductFilterValues,
  ProductStatusFilter,
} from "../../models";

// QPilot types
import { ProductGroup } from "../../../../core/services/qpilot.service";

/**
 * Status filter option interface
 */
interface StatusOption {
  value: ProductStatusFilter;
  label: string;
}

/**
 * Products Filters Component
 * Filters: Search, Status dropdown, Product Group dropdown
 * (Stock Level and Product Types removed - not in old QMC)
 */
@Component({
  selector: "app-products-filters",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    SelectModule,
  ],
  templateUrl: "./products-filters.component.html",
  styleUrl: "./products-filters.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductsFiltersComponent implements OnInit, OnDestroy {
  // Input/Output pattern
  @Input() filterForm!: FormGroup<IProductFilterControls>;
  @Input() counts: IProductCounts | null = null;
  @Input() loading = false;
  @Input() drawerMode = false;
  @Input() productGroups: ProductGroup[] = [];

  @Output() filtersChanged = new EventEmitter<IProductFilterValues>();
  @Output() clearFilters = new EventEmitter<void>();

  // Component state
  private readonly destroy$ = new Subject<void>();

  // Status dropdown options
  readonly statusSelectOptions: StatusOption[] = [
    { value: "all", label: "All Statuses" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "invalid", label: "Invalid" },
  ];

  /**
   * Check if any filters are active
   */
  get hasActiveFilters(): boolean {
    if (!this.filterForm) return false;

    const formValue = this.filterForm.value;
    return !!(
      formValue.search?.trim() ||
      (formValue.status && formValue.status !== "all") ||
      formValue.productGroupId
    );
  }

  ngOnInit(): void {
    this.setupFormSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Setup form subscriptions for real-time filtering
   */
  private setupFormSubscriptions(): void {
    if (!this.filterForm) return;

    // Debounce search input
    this.filterForm.controls.search.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.emitFilterChange();
      });

    // Status filter - immediate
    this.filterForm.controls.status.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.emitFilterChange();
      });

    // Product group filter - immediate
    this.filterForm.controls.productGroupId.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.emitFilterChange();
      });
  }

  /**
   * Emit filter changes to parent component
   */
  private emitFilterChange(): void {
    if (!this.filterForm) return;

    const formValue = this.filterForm.value;
    const filter: IProductFilterValues = {
      search: formValue.search?.trim() || undefined,
      status: formValue.status ?? "all",
      productGroupId: formValue.productGroupId ?? undefined,
    };

    this.filtersChanged.emit(filter);
  }

  /**
   * Clear all filters
   */
  onClearFilters(): void {
    if (!this.filterForm) return;

    this.filterForm.patchValue({
      search: null,
      status: "all",
      productGroupId: null,
    });

    this.clearFilters.emit();
  }

  /**
   * Handle status clear button click
   */
  onStatusClear(): void {
    this.filterForm.controls.status.setValue("all");
  }
}
